/**
 * API client for the Pro-app signup page (`/join`, plan 476 M3).
 *
 * The public reads/validate carry no token. `/api/pro-admission/join` is
 * ClerkAuth (deliberately NOT `/api/professional`, whose RequirePro would
 * refuse a user with no pro role yet). The pro role comes from `/api/users/me`
 * (the DB role `ProvisionAgentProfile` writes), never from Clerk metadata.
 */
import { getJSON, postJSON } from "./api";

export type JoinProType = "agent" | "lo";
export type JoinPolicy = "code_only" | "code_or_referral" | "open";

/** The redeem body (also the validate body); `email` is only sent to validate. */
export interface JoinRequest {
  pro_type?: JoinProType;
  service_area?: string;
  /** Google place id of a picked typeahead suggestion; absent for free text. */
  service_area_place_id?: string;
  license_num?: string;
  license_state?: string;
  nmls_number?: string;
  code?: string;
  pro_referral_token?: string;
  campaign_credential?: string;
  brokerage?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface JoinValidateResponse {
  valid: boolean;
  service_area_label?: string;
  service_area_launched: boolean | null;
}

export interface JoinResponse {
  redemption_id: string;
  status: "provisioned";
  pro_type: JoinProType;
  service_area?: string;
  service_area_launched: boolean | null;
  created: boolean;
  lane: "open" | "code" | "referral";
}

export interface JoinPolicyResponse {
  policy: JoinPolicy;
  code_required: boolean;
}

export interface JoinRequirementsResponse {
  credential_required: boolean;
  credential_kind: "re_license" | "nmls" | null;
  pro_type: JoinProType | null;
  license_state_required: boolean;
}

export interface JoinRequirementsRequest {
  code?: string;
  pro_type?: JoinProType;
  pro_referral_token?: string;
  campaign_credential?: string;
}

export function validateJoin(req: JoinRequest): Promise<JoinValidateResponse> {
  return postJSON<JoinValidateResponse>("/api/public/pro-admission/validate", req);
}

export function joinProAdmission(req: JoinRequest, token: string): Promise<JoinResponse> {
  return postJSON<JoinResponse>("/api/pro-admission/join", req, token);
}

export function getJoinPolicy(): Promise<JoinPolicyResponse> {
  return getJSON<JoinPolicyResponse>("/api/public/pro-admission/policy");
}

export function getJoinRequirements(
  req: JoinRequirementsRequest,
): Promise<JoinRequirementsResponse> {
  return postJSON<JoinRequirementsResponse>("/api/public/pro-admission/requirements", req);
}

/** The signed-in user's DB role, or null when it cannot be read. */
export async function getCurrentUserRole(token: string): Promise<string | null> {
  const me = await getJSON<{ role?: string }>("/api/users/me", token);
  return me.role ?? null;
}

/** agent | lo — the roles RequirePro admits; admin is not a pro. */
export function isProRole(role: string | null | undefined): boolean {
  return role === "agent" || role === "lo";
}

/** The calls the /join flow makes; tests and IS_PREVIEW inject their own. */
export interface JoinApi {
  validate: typeof validateJoin;
  join: typeof joinProAdmission;
  policy: typeof getJoinPolicy;
  requirements: typeof getJoinRequirements;
  role: typeof getCurrentUserRole;
}

export const realJoinApi: JoinApi = {
  validate: validateJoin,
  join: joinProAdmission,
  policy: getJoinPolicy,
  requirements: getJoinRequirements,
  role: getCurrentUserRole,
};

export type JoinCodeMode = "signup" | "signin";

export type JoinAuthStart =
  | { kind: "code_sent" }
  /** The email already has a Clerk account: switch to email-code sign-in. */
  | { kind: "exists" }
  | { kind: "error"; message: string };

export type JoinAuthVerify =
  | { ok: true }
  | { ok: false; message: string; needsLogin?: boolean };

/** The Clerk surface the /join flow needs, behind one seam (no Clerk under IS_PREVIEW or in tests). */
export interface JoinAuth {
  isLoaded: boolean;
  isSignedIn: boolean;
  getToken: () => Promise<string | null>;
  startSignUp: (input: {
    email: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<JoinAuthStart>;
  startSignIn: (email: string) => Promise<JoinAuthStart>;
  /** Verifies the emailed code and activates the session. */
  verify: (mode: JoinCodeMode, code: string) => Promise<JoinAuthVerify>;
}
